import React from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  FormHelperText,
  Box,
  Chip,
  InputAdornment,
  Radio,
  RadioGroup,
  CircularProgress,
  Tooltip,
  Switch,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { FieldConfig, FieldType } from '../types/contractForm.types';
import { ControllerRenderProps, ControllerFieldState } from 'react-hook-form';

/**
 * Props for the FieldRenderer component
 */
interface FieldRendererProps {
  /** Field configuration from ContractFormConfig */
  field: FieldConfig;
  /** React Hook Form field props */
  fieldProps: ControllerRenderProps;
  /** React Hook Form field state (errors, etc.) */
  fieldState: ControllerFieldState;
  /** RAG suggestions for this field */
  suggestions?: string[];
  /** RAG loading state */
  ragLoading?: boolean;
}

/**
 * FieldRenderer Component
 *
 * Renders different field types based on FieldConfig.type configuration.
 * Handles validation errors, helper text, RAG suggestions, and responsive layouts.
 *
 * @component
 * @example
 * ```tsx
 * <Controller
 *   name="email"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <FieldRenderer
 *       field={emailFieldConfig}
 *       fieldProps={field}
 *       fieldState={fieldState}
 *     />
 *   )}
 * />
 * ```
 */
export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  fieldProps,
  fieldState,
  suggestions,
  ragLoading
}) => {
  const { error } = fieldState;
  const helperText = field.helperText || field.helpText;

  /**
   * Translate GridProps into CSS grid column spans for responsive layouts.
   * Falls back to a full-width span when a breakpoint-specific value is missing.
   */
  const getGridColumnStyles = () => {
    const { gridProps } = field;

    // Force email inputs to full width across breakpoints for better visibility
    if (field.type === FieldType.EMAIL) {
      return {
        gridColumn: {
          xs: 'span 12',
          sm: 'span 12',
          md: 'span 12',
          lg: 'span 12',
          xl: 'span 12',
        },
      };
    }

    const xsSpan = gridProps?.xs ?? 12;
    const smSpan = gridProps?.sm ?? xsSpan;
    const mdSpan = gridProps?.md ?? smSpan;
    const lgSpan = gridProps?.lg ?? mdSpan;
    const xlSpan = gridProps?.xl ?? lgSpan;

    return {
      gridColumn: {
        xs: `span ${xsSpan}`,
        sm: `span ${smSpan}`,
        md: `span ${mdSpan}`,
        lg: `span ${lgSpan}`,
        xl: `span ${xlSpan}`,
      },
    };
  };

  const gridColumnStyles = getGridColumnStyles();

  /**
   * Apply RAG suggestion to field
   */
  const applySuggestion = (suggestion: string) => {
    fieldProps.onChange(suggestion);
  };

  /**
   * Render the appropriate field component based on field type
   */
  const renderField = () => {
    switch (field.type) {
      case FieldType.TEXT:
      case FieldType.EMAIL:
      case FieldType.TEL:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            type={field.type}
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            autoComplete={field.autoComplete}
            InputProps={{
              readOnly: field.readOnly,
              endAdornment: helperText && !error ? (
                <InputAdornment position="end" sx={{ mr: 0.5 }}>
                  <Tooltip 
                    title={helperText} 
                    placement="bottom-end"
                    slotProps={{
                      popper: {
                        modifiers: [
                          { name: 'offset', options: { offset: [0, 12] } },
                          { name: 'preventOverflow', options: { padding: 8, altBoundary: true, tether: true } },
                        ],
                      }
                    }}
                  >
                    <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
                      <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Box>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
            inputProps={field.inputProps}
          />
        );

      case FieldType.MULTILINE:
      case FieldType.TEXTAREA:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            multiline
            rows={field.rows || 4}
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
          />
        );

      case FieldType.SELECT:
        return (
          <FormControl fullWidth error={!!error} disabled={field.disabled}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              {...fieldProps}
              label={field.label}
              multiple={field.multiple}
              readOnly={field.readOnly}
            >
              {field.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {(error || helperText) && (
              <FormHelperText>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case FieldType.MULTISELECT:
        return (
          <FormControl fullWidth error={!!error} disabled={field.disabled}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              {...fieldProps}
              label={field.label}
              multiple
              readOnly={field.readOnly}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const option = field.options?.find((opt) => opt.value === value);
                    return (
                      <Chip
                        key={value}
                        label={option?.label || value}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {field.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {(error || helperText) && (
              <FormHelperText>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case FieldType.NUMBER:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            type="number"
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
            inputProps={{
              min: field.min,
              max: field.max,
              step: field.step,
              ...field.inputProps,
            }}
          />
        );

      case FieldType.CURRENCY:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            type="number"
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
              endAdornment: (
                <InputAdornment position="end">
                  {field.currency || 'Kč'}
                </InputAdornment>
              ),
            }}
            inputProps={{
              min: field.min || 0,
              max: field.max,
              step: field.step || (field.decimalPlaces === 0 ? 1 : 0.01),
              ...field.inputProps,
            }}
          />
        );

      case FieldType.PERCENTAGE:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            type="number"
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{
              min: field.min || 0,
              max: field.max || 100,
              step: field.step || 1,
              ...field.inputProps,
            }}
          />
        );

      case FieldType.DATE:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            type="date"
            InputLabelProps={{ shrink: true }}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
          />
        );

      case FieldType.CHECKBOX:
        return (
          <FormControl error={!!error} disabled={field.disabled}>
            <FormControlLabel
              control={
                <Switch
                  name={fieldProps.name}
                  inputRef={fieldProps.ref}
                  checked={Boolean(fieldProps.value)}
                  onChange={(e) => fieldProps.onChange(e.target.checked)}
                  onBlur={fieldProps.onBlur}
                  disabled={field.disabled}
                  readOnly={field.readOnly}
                />
              }
              label={field.label}
            />
            {(error || helperText) && (
              <FormHelperText>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case FieldType.RADIO:
        return (
          <FormControl error={!!error} disabled={field.disabled}>
            <InputLabel shrink>{field.label}</InputLabel>
            <RadioGroup {...fieldProps} sx={{ mt: 2 }}>
              {field.options?.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={
                    <>
                      {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}
                      {opt.label}
                    </>
                  }
                  disabled={opt.disabled}
                />
              ))}
            </RadioGroup>
            {(error || helperText) && (
              <FormHelperText>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );

      case FieldType.FILE:
        return (
          <FormControl fullWidth error={!!error} disabled={field.disabled}>
            <TextField
              type="file"
              label={field.label}
              InputLabelProps={{ shrink: true }}
              helperText={error?.message || helperText}
              error={!!error}
              disabled={field.disabled}
              inputProps={{
                accept: field.accept,
                ...field.inputProps,
              }}
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];

                // Validate file size if maxFileSize is set
                if (file && field.maxFileSize && file.size > field.maxFileSize) {
                  // TODO: Set custom error - would need access to setError from RHF
                  console.error(`File size exceeds maximum of ${field.maxFileSize} bytes`);
                  return;
                }

                fieldProps.onChange(file);
              }}
            />
          </FormControl>
        );

      case FieldType.VIN:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            placeholder={field.placeholder || 'např. WAUZZZ4G7EN123456'}
            helperText={error?.message || helperText || '17místný identifikační kód vozidla'}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
            inputProps={{
              maxLength: 17,
              style: { textTransform: 'uppercase' },
              ...field.inputProps,
            }}
          />
        );

      case FieldType.REGISTRATION_PLATE:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            placeholder={field.placeholder || 'např. 1A2 3456'}
            helperText={error?.message || helperText || 'České registrační číslo'}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
            inputProps={{
              style: { textTransform: 'uppercase' },
              ...field.inputProps,
            }}
          />
        );

      case FieldType.ICO:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            placeholder={field.placeholder || '12345678'}
            helperText={error?.message || helperText || 'Identifikační číslo osoby (8 číslic)'}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
            inputProps={{
              maxLength: 8,
              pattern: '[0-9]{8}',
              ...field.inputProps,
            }}
          />
        );

      case FieldType.DIC:
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            placeholder={field.placeholder || 'CZ12345678'}
            helperText={error?.message || helperText || 'Daňové identifikační číslo'}
            error={!!error}
            fullWidth
            disabled={field.disabled}
            InputProps={{
              readOnly: field.readOnly,
            }}
            inputProps={{
              style: { textTransform: 'uppercase' },
              ...field.inputProps,
            }}
          />
        );

      default:
        // Fallback to text field for unknown types
        return (
          <TextField
            {...fieldProps}
            label={field.label}
            placeholder={field.placeholder}
            helperText={error?.message || helperText}
            error={!!error}
            fullWidth
            disabled={field.disabled}
          />
        );
    }
  };

  /**
   * Render RAG suggestions if available
   */
  const renderRagSuggestions = () => {
    if (!field.ragEnabled || (!suggestions?.length && !ragLoading)) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {ragLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <FormHelperText>Načítání AI doporučení...</FormHelperText>
          </Box>
        )}

        {suggestions && suggestions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <FormHelperText sx={{ mb: 0.5 }}>
              AI doporučení:
            </FormHelperText>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {suggestions.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  size="small"
                  onClick={() => applySuggestion(suggestion)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Render field with grid layout
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        ...gridColumnStyles,
      }}
    >
      {renderField()}
      {renderRagSuggestions()}
    </Box>
  );
};

export default FieldRenderer;
